import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AccountManagerSelector } from './AccountManagerSelector';
import { CampaignStatusSelect } from './CampaignStatusSelect';
import { MemberAvatar } from './atoms/AdminPrimitives';
import { OutreachTypeBadge } from '../atoms/OutreachTypeBadge/OutreachTypeBadge';
import { n8nService } from '../core/services/n8n.service';
import { useAuth } from '../../contexts/AuthContext';
import { normalizeManagerId, resolveCampaignOwner } from './teamOwnership';
import type { CampaignMetadata } from '../../types';
import type { TeamMemberData } from '../../types/n8nResponses';
import { getCampaignDiscardedLeadCount, getCampaignVisibleLeadCount, getStatusCountsFromAggregate } from '../../utils/campaignMetrics';
import { normalizeCampaignLifecycleStatus } from '../../utils/campaignLifecycleStatus';

const STATUS_PILL: Record<string, string> = {
  active: 'border-emerald-400/20 bg-emerald-400/10 text-emerald-100',
  paused: 'border-amber-400/20 bg-amber-400/10 text-amber-100',
  stopped: 'border-rose-400/25 bg-rose-400/10 text-rose-100',
  'asking to human': 'border-violet-400/25 bg-violet-400/10 text-violet-100',
  archived: 'border-white/12 bg-white/[0.04] text-white/50',
};

/**
 * Pill colour for a campaign. Routes through the SHARED lifecycle normalizer so
 * the colour can never disagree with the pill's own text — the old local
 * `normalizeStatus` mapped `'archive'` to the grey archived pill while the
 * select's separate lookup had no `archive` key and fell back to "Active",
 * producing a grey pill reading "Active".
 */
const resolvePillClass = (value: string | null | undefined): string => {
  const raw = String(value ?? '').trim().toLowerCase();
  // System-managed states keep their own tone; they are not lifecycle buckets.
  if (raw === 'stopped') return STATUS_PILL.stopped;
  if (raw === 'asking to human') return STATUS_PILL['asking to human'];
  // DONE/COMPLETED reads Active per the shared contract, but the Team board
  // still tints it differently so finished work stands out from live work.
  if (raw === 'completed' || raw === 'done') return 'border-sky-400/20 bg-sky-400/10 text-sky-100';
  return STATUS_PILL[normalizeCampaignLifecycleStatus(value) ?? 'active'];
};

const CAMPAIGN_PULSE_STAGES = [
  { key: 'new_lead', label: 'New Lead', color: '#7DD3FC' },
  { key: 'enriching', label: 'Enriching', color: '#38BDF8' },
  { key: 'enriched', label: 'Enriched', color: '#67E8F9' },
  { key: 'outreaching', label: 'Outreaching', color: '#818CF8' },
  { key: 'outreached', label: 'Outreached', color: '#A78BFA' },
  { key: 'engaged', label: 'Engaged', color: '#F59E0B' },
  { key: 'proposal', label: 'Proposal', color: '#06B6D4' },
  { key: 'converted', label: 'Converted', color: '#34D399' },
  { key: 'discarded', label: 'Discarded', color: '#7F1D1D' },
] as const;

interface AdminCampaignCardProps {
  campaign: CampaignMetadata;
  teamMembers: TeamMemberData[];
  variant?: 'grid' | 'list' | 'compact';
  onOpenCampaign?: (campaignId: string) => void;
  onOpenDetails?: (campaign: CampaignMetadata) => void;
  /** Seller (or team, when unassigned) label shown as an eyebrow above the name. */
  sellerLabel?: string | null;
  /** Opens the Report Generator locked to this campaign (Team view "+Report"). */
  onGenerateReport?: (campaignId: string) => void;
}

const DataLabel: React.FC<{ label: string; value: React.ReactNode; strong?: boolean }> = ({
  label,
  value,
  strong = false,
}) => (
  <div className="min-w-0">
    <div className="text-[9px] font-black uppercase tracking-[0.16em] text-[#68758F]">{label}</div>
    <div className={`mt-1 truncate ${strong ? 'text-[18px] font-black leading-none tabular-nums text-white' : 'text-[12px] font-bold text-white/82'}`}>
      {value}
    </div>
  </div>
);

/** Inline-editable weekly limit for compact rows: click the value → number
 *  input → Enter/blur saves via update_campaign. Esc cancels. */
export const EditableWeeklyLimit: React.FC<{ campaignId: string; weeklyLimit: number | string | null | undefined; disabled?: boolean; onSaved?: (limit: number) => void }> = ({ campaignId, weeklyLimit, disabled = false, onSaved }) => {
  const { currentUser, targetEmail } = useAuth();
  const email = targetEmail || currentUser?.email || '';
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(weeklyLimit ?? ''));
  const [savedLimit, setSavedLimit] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mutation = useMutation({
    mutationFn: (limit: number) =>
      n8nService.updateCampaign({
        campaign_id: campaignId,
        client_email: email,
        weekly_limit: limit,
      }),
    // Show the new value immediately (the upstream store syncs ~1 min behind
    // a 200, so a refetch would otherwise serve the old number back) and keep
    // the team list cache in step so the Overview totals update too.
    onSuccess: (_response, limit) => {
      setSavedLimit(limit);
      onSaved?.(limit);
      queryClient.setQueriesData<{ campaigns: CampaignMetadata[] }>({ queryKey: ['campaigns', 'all'] }, (old) =>
        old?.campaigns
          ? { ...old, campaigns: old.campaigns.map((item) => item.id === campaignId ? { ...item, weekly_limit: limit } : item) }
          : old
      );
    },
    onError: () => setSavedLimit(null),
  });

  useEffect(() => {
    if (editing) {
      setDraft(String(weeklyLimit ?? ''));
      window.requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [editing, weeklyLimit]);

  const commit = () => {
    setEditing(false);
    const parsed = Math.floor(Number(draft));
    if (!draft.trim() || Number.isNaN(parsed) || parsed < 0 || parsed === Number(weeklyLimit ?? 0)) return;
    // Optimistic: render the typed value the instant Enter is pressed — the
    // n8n round-trip takes ~1-2s and the old number must not sit there that
    // long. onError rolls it back.
    setSavedLimit(parsed);
    mutation.mutate(parsed);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={0}
        autoFocus
        value={draft}
        disabled={mutation.isPending}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); commit(); }
          if (event.key === 'Escape') { event.preventDefault(); setEditing(false); }
        }}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        aria-label={`Weekly limit for campaign ${campaignId}`}
        className="team-campaign-metric-value w-16 rounded-[10px] border border-cyan-300/50 bg-black/50 px-2.5 py-1 text-cyan-200 outline-none focus:ring-2 focus:ring-cyan-300/25"
      />
    );
  }

  return (
    <button
      type="button"
      title={disabled ? 'Weekly limit' : 'Click to edit the weekly limit'}
      disabled={disabled || mutation.isPending}
      onClick={(event) => { event.stopPropagation(); setEditing(true); }}
      className="team-campaign-metric group/wl cursor-pointer rounded-[10px] border border-cyan-300/25 bg-cyan-400/[0.07] px-2.5 py-1 transition-all hover:border-cyan-300/50 hover:bg-cyan-400/[0.14]"
    >
      <span className="team-campaign-metric-value text-cyan-200 group-hover/wl:text-white">
        {(savedLimit ?? (typeof weeklyLimit === 'number' ? weeklyLimit : Number(weeklyLimit) || 0)).toLocaleString()}
        <Icon icon="bx:pencil" className="ml-1 inline h-2.5 w-2.5 opacity-60" aria-hidden="true" />
        {mutation.isPending && <Icon icon="bx:loader-circle" className="ml-1 inline h-3 w-3 animate-spin" aria-hidden="true" />}
      </span>
      <span className="team-campaign-metric-label text-cyan-200/60">Weekly</span>
      {mutation.isError && <span role="alert" className="sr-only">{(mutation.error as Error)?.message || 'Save failed'}</span>}
    </button>
  );
};

const ActionButtons: React.FC<{
  campaign: CampaignMetadata;
  onOpenCampaign?: (campaignId: string) => void;
  onOpenDetails?: (campaign: CampaignMetadata) => void;
}> = ({ campaign, onOpenCampaign, onOpenDetails }) => {
  if (!onOpenCampaign && !onOpenDetails) return null;

  return (
    <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row">
      {onOpenCampaign && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenCampaign(campaign.id);
          }}
          className="inline-flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[12px] border border-cyan-400/20 bg-cyan-400/10 px-3 text-[10px] font-black uppercase tracking-[0.14em] text-cyan-100 transition-colors hover:bg-cyan-400/15"
        >
          <Icon icon="solar:eye-bold" className="h-4 w-4" aria-hidden="true" />
          <span className="truncate">Open</span>
        </button>
      )}
      {onOpenDetails && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenDetails(campaign);
          }}
          className="inline-flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[12px] border border-white/10 bg-white/[0.05] px-3 text-[10px] font-black uppercase tracking-[0.14em] text-white/82 transition-colors hover:bg-white/[0.1]"
        >
          <Icon icon="solar:document-text-bold" className="h-4 w-4" aria-hidden="true" />
          <span className="truncate">Details</span>
        </button>
      )}
    </div>
  );
};

export const AdminCampaignCard: React.FC<AdminCampaignCardProps> = ({
  campaign,
  teamMembers,
  variant = 'grid',
  onOpenCampaign,
  onOpenDetails,
  sellerLabel,
  onGenerateReport,
}) => {
  const pillClass = resolvePillClass(campaign.status);
  const leadCount = getCampaignVisibleLeadCount(campaign);
  const discardedLeadCount = getCampaignDiscardedLeadCount(campaign);
  // Normalized buckets via the SHARED aggregate reader — raw n8n keys like
  // `summarizing` (→ ENRICHING) count here exactly as in the Team overview
  // graph; a raw `.enriching` lookup silently read 0 for those leads.
  const statusCounts = useMemo(
    () => getStatusCountsFromAggregate(campaign.leads_by_status),
    [campaign.leads_by_status]
  );
  const discardedLeadDeltaLabel = discardedLeadCount > 0
    ? `-${discardedLeadCount.toLocaleString()}`
    : null;
  const explicitManagerId = normalizeManagerId(campaign.account_manager_id);
  const needsAssignment = !explicitManagerId;
  const isUnassigned = needsAssignment;
  const manager = useMemo(
    () => resolveCampaignOwner(campaign, teamMembers, campaign.client_email),
    [campaign, teamMembers]
  );

  const channelLabels = Array.isArray(campaign.outreach_channel)
    ? campaign.outreach_channel
        .slice(0, 3)
        .map((channel) => channel?.value || channel?.id)
        .filter(Boolean)
    : [];

  const objectiveText = campaign.primary_campaign_objective || campaign.target_audience_niche || campaign.client_name || 'Campaign overview';
  const ownerLabel = manager?.name || manager?.email || 'Account owner';
  const ownerMeta = needsAssignment ? 'Needs explicit assignment' : manager?.name ? manager.email : 'Team owner';
  // Real profile picture with initials fallback (broken link → initials);
  // unassigned campaigns show a dash placeholder instead of an avatar.
  const ownerAvatar = !isUnassigned ? (
    <MemberAvatar
      name={manager?.name}
      email={manager?.email}
      imageUrl={manager?.profile_picture_url}
      className="h-12 w-12 rounded-full border border-cyan-300/18 text-sm font-black"
    />
  ) : null;
  const updatedLabel = (() => {
    const rawValue = campaign.updatedAt || campaign.createdAt || campaign.created;
    if (!rawValue) return 'Not set';
    try {
      return new Date(rawValue).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return String(rawValue);
    }
  })();
  const locationLabel = campaign.search_location?.trim() || 'No location';
  const weeklyLimitLabel = campaign.weekly_limit ? `${campaign.weekly_limit} leads` : 'Not set';
  const pulseStages = useMemo(
    () => CAMPAIGN_PULSE_STAGES.map((stage) => ({
      ...stage,
      // SHARED normalizer: raw n8n keys (`summarizing`, `message_ready`, …)
      // land in the same bucket the graph and metric cells read.
      value: statusCounts[stage.key as keyof typeof statusCounts] ?? 0,
    })),
    [statusCounts]
  );
  const pulseTotal = useMemo(
    () => pulseStages.reduce((sum, stage) => sum + stage.value, 0),
    [pulseStages]
  );
  const pulseTotalLabel = pulseTotal.toLocaleString();
  const pulseTotalClassName = pulseTotalLabel.length >= 5
    ? 'text-[16px]'
    : pulseTotalLabel.length === 4
      ? 'text-[18px]'
      : pulseTotalLabel.length === 3
        ? 'text-[20px]'
        : 'text-[24px]';
  const pulseSegments = useMemo(() => {
    if (pulseTotal <= 0) return [];

    const radius = 42;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;

    return pulseStages
      .filter((stage) => stage.value > 0)
      .map((stage) => {
        const segmentLength = (stage.value / pulseTotal) * circumference;
        const segment = {
          ...stage,
          dashArray: `${segmentLength} ${circumference - segmentLength}`,
          dashOffset: -offset,
        };
        offset += segmentLength;
        return segment;
      });
  }, [pulseStages, pulseTotal]);
  const isCampaignInteractive = typeof onOpenCampaign === 'function';
  const handleOpenCampaign = () => {
    onOpenCampaign?.(campaign.id);
  };
  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (!isCampaignInteractive) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenCampaign();
    }
  };
  const cardClassName = variant === 'list'
    ? `group relative overflow-hidden rounded-2xl border border-white/8 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.04),transparent_26%),linear-gradient(180deg,rgba(16,18,22,0.98),rgba(8,9,12,0.98))] p-5 shadow-[0_18px_42px_rgba(0,0,0,0.24)] transition-all duration-300 hover:border-cyan-300/24 ${isCampaignInteractive ? 'cursor-pointer focus:outline-none focus-visible:border-cyan-300/34' : ''}`
    : `group relative overflow-hidden rounded-2xl border border-white/8 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.05),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.04),transparent_26%),linear-gradient(180deg,rgba(17,19,24,0.98),rgba(8,9,12,0.98))] p-5 shadow-[0_22px_46px_rgba(0,0,0,0.28)] transition-all duration-300 hover:-translate-y-1 hover:border-cyan-300/26 ${isCampaignInteractive ? 'cursor-pointer focus:outline-none focus-visible:border-cyan-300/34' : ''}`;

  if (variant === 'compact') {
    // Compact = directory row. Column headers live in the board container
    // (.team-campaign-columns). The whole row opens the campaign (onClick);
    // metrics live in one grouped block; actions are icon buttons + the white
    // "+ Report" primary at the far right.
    return (
      <article
        className="team-campaign-entry-compact group relative overflow-hidden py-4 transition-all duration-200 cursor-pointer"
        onClick={isCampaignInteractive ? handleOpenCampaign : undefined}
        onKeyDown={handleCardKeyDown}
        role={isCampaignInteractive ? 'button' : undefined}
        tabIndex={isCampaignInteractive ? 0 : undefined}
        aria-label={isCampaignInteractive ? `Open ${campaign.name}` : undefined}
      >
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(34,211,238,0.03),transparent_38%)] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
        {/* Row grid: Campaign | Status | Metrics | Assigned | Actions.
            The Status column hugs its pill (~112px — the old 135px left a
            visible dead zone before Metrics) and the inter-column gap is
            tight, so the metrics labels keep room without cropping.
            Campaign/Actions columns keep their exact sizes. */}
        <div className="relative grid grid-cols-1 items-center gap-2.5 xl:grid-cols-[minmax(170px,0.85fr)_112px_minmax(0,1.85fr)_152px_auto] xl:gap-3">
          {/* Campaign — seller/team eyebrow sits ALWAYS above the name */}
          <div className="min-w-0">
            {sellerLabel && (
              <p className="truncate text-[11px] font-medium tracking-[0.01em] text-[#8B96B0]">
                {sellerLabel}
              </p>
            )}
            <h3 className="line-clamp-1 text-[14px] font-semibold leading-5 text-white">
              {campaign.name}
            </h3>
            <p className="mt-0.5 text-[12px] text-[#9999a6]">
              {needsAssignment ? 'Needs assignment · ' : ''}Updated {updatedLabel}
            </p>
          </div>

          {/* Status */}
          <CampaignStatusSelect campaign={campaign} className={`w-fit ${pillClass}`} />

          {/* ONE grouped stats block, funnel order: Total | Enriching |
              Enriched (cumulative) | Outreaching | Outreached | Engaged |
              Discard | Weekly (editable, tinted to read as interactive).
              Cell colours mirror STATUS_MAP (design-system source of truth).
              All cells live INSIDE this block so the row's other columns
              (name / status / assigned / actions) never change width. */}
          <div className="team-campaign-metrics">
            <div className="team-campaign-metric">
              <span className="team-campaign-metric-value text-white">{leadCount.toLocaleString()}</span>
              <span className="team-campaign-metric-label">Total</span>
            </div>
            <span className="team-campaign-metric-sep" />
            <div className="team-campaign-metric">
              <span className="team-campaign-metric-value text-[#FBBF24]">{statusCounts.enriching.toLocaleString()}</span>
              <span className="team-campaign-metric-label">Enriching</span>
            </div>
            <span className="team-campaign-metric-sep" />
            <div className="team-campaign-metric">
              {/* CUMULATIVE enriched (enriched + outreaching + outreached + …),
                  matching the detail view, the My Campaigns card and the
                  overview graph — a lead past ENRICHED is still enriched. The
                  raw bucket undercounted by every lead already in outreach
                  (Team card said 10 while the campaign detail said 11). */}
              <span className="team-campaign-metric-value text-[#3B82F6]">{statusCounts.enriched_cumulative.toLocaleString()}</span>
              <span className="team-campaign-metric-label">Enriched</span>
            </div>
            <span className="team-campaign-metric-sep" />
            <div className="team-campaign-metric">
              <span className="team-campaign-metric-value text-[#F97316]">{statusCounts.outreaching.toLocaleString()}</span>
              <span className="team-campaign-metric-label">Outreaching</span>
            </div>
            <span className="team-campaign-metric-sep" />
            <div className="team-campaign-metric">
              <span className="team-campaign-metric-value text-[#A855F7]">{statusCounts.outreached.toLocaleString()}</span>
              <span className="team-campaign-metric-label">Outreached</span>
            </div>
            <span className="team-campaign-metric-sep" />
            <div className="team-campaign-metric">
              <span className="team-campaign-metric-value text-[#EC4899]">{statusCounts.engaged.toLocaleString()}</span>
              <span className="team-campaign-metric-label">Engaged</span>
            </div>
            <span className="team-campaign-metric-sep" />
            <div className="team-campaign-metric">
              <span className="team-campaign-metric-value text-white/45">
                {discardedLeadCount > 0 ? `-${discardedLeadCount.toLocaleString()}` : '0'}
              </span>
              <span className="team-campaign-metric-label">Discard</span>
            </div>
            <span className="team-campaign-metric-sep" />
            <EditableWeeklyLimit campaignId={campaign.id} weeklyLimit={campaign.weekly_limit} />
          </div>

          {/* Assigned */}
          <AccountManagerSelector
            campaignId={campaign.id}
            currentManagerId={campaign.account_manager_id}
            teamMembers={teamMembers}
            variant="compact"
          />

          {/* Actions — "+ Report" primary first, then the icon-only "info"
              details button as the last element. The whole row opens the
              campaign on click, so there is no separate Open button. */}
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onGenerateReport?.(campaign.id);
              }}
              className="inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-[10px] bg-white px-3 text-[10px] font-black uppercase tracking-[0.1em] text-[#0B0B0D] transition-all hover:bg-[#e9ecff] disabled:cursor-not-allowed disabled:opacity-60"
              title="Generate the campaign report"
            >
              <Icon icon="solar:document-add-bold" className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              + Report
            </button>
            {onOpenDetails && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenDetails(campaign);
                }}
                className="team-action inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-[10px] px-2.5 text-[9px] font-bold uppercase tracking-[0.06em]"
                title="Campaign details"
                aria-label="Campaign details"
              >
                <Icon icon="solar:info-circle-bold" className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Info</span>
              </button>
            )}
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'list') {
    return (
      <article
        className={cardClassName}
        onClick={handleOpenCampaign}
        onKeyDown={handleCardKeyDown}
        role={isCampaignInteractive ? 'button' : undefined}
        tabIndex={isCampaignInteractive ? 0 : undefined}
        aria-label={isCampaignInteractive ? `Open ${campaign.name}` : undefined}
      >
        <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.016))] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <div className="relative flex flex-col gap-4 xl:grid xl:grid-cols-[280px_minmax(0,1fr)] xl:gap-5">
          <div className="min-w-0 rounded-2xl px-5 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] xl:flex xl:min-h-full xl:flex-col">
            <div className="flex flex-wrap items-center gap-2.5">
              <CampaignStatusSelect campaign={campaign} className={pillClass} />
              {isUnassigned && (
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-amber-100">
                  Needs owner
                </span>
              )}
              {campaign.client_email && (
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/55">
                  {campaign.client_email}
                </span>
              )}
            </div>

            {sellerLabel && (
              <p className="mt-4 truncate text-[12px] font-medium tracking-[0.01em] text-[#8B96B0]">
                {sellerLabel}
              </p>
            )}
            <h3 className="text-[26px] font-black leading-[1.05] text-white transition-colors group-hover:text-[#E7EBFF]">
              {campaign.name}
            </h3>
            <p className="mt-3 text-[17px] font-medium leading-6 text-[#E5E7F2]">
              {objectiveText}
            </p>
            <p className="mt-1 text-[10px] font-medium text-[#70768E]">ID: {campaign.id}</p>

            <div className="mt-6 grid grid-cols-1 gap-3 pt-4 text-[11px] text-[#8A8FA9] xl:mt-auto">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span>{locationLabel}</span>
                <span>Updated {updatedLabel}</span>
              </div>
            </div>
          </div>

          <div className="min-w-0 rounded-2xl p-2.5 shadow-[0_20px_40px_rgba(0,0,0,0.26)]">
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_320px] xl:gap-4">
              <div className="min-w-0 rounded-xl bg-white/[0.015] p-2.5">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(120px,0.5fr)_minmax(170px,0.7fr)_minmax(0,1.4fr)]">
                  <div className="px-4 pb-5 pt-3">
                    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E]">
                      Total Leads
                    </div>
                    <div className="mt-3 text-[34px] font-black leading-none text-white tabular-nums">
                      {leadCount.toLocaleString()}
                    </div>
                    {discardedLeadDeltaLabel && (
                      <div className="mt-2 text-[11px] font-black uppercase tracking-[0.14em] text-[#7F1D1D] tabular-nums">
                        {discardedLeadDeltaLabel} discarded
                      </div>
                    )}
                  </div>

                  <div className="px-4 pb-5 pt-3">
                    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E]">
                      Weekly Limit
                    </div>
                    <div className="mt-3 rounded-[14px] border border-white/10 bg-white/[0.05] px-3 py-4 text-[11px] font-black uppercase tracking-[0.16em] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                      {weeklyLimitLabel}
                    </div>
                  </div>

                  <div className="min-w-0 border-l border-white/8 px-4 pb-4 pt-3">
                    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E]">
                      Campaign Channels
                    </div>
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      {channelLabels.length > 0 ? (
                        channelLabels.map((label) => (
                          <button
                            key={`${campaign.id}-${label}`}
                            type="button"
                            onClick={(event) => event.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-cyan-100 transition-colors hover:text-cyan-200"
                          >
                            <Icon icon="solar:hashtag-square-linear" className="h-3.5 w-3.5 text-cyan-300" aria-hidden="true" />
                            <span>{label}</span>
                          </button>
                        ))
                      ) : (
                        <div className="text-[11px] font-medium text-gray-500">
                          No outreach channels configured
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-1 border-t border-white/8 px-4 pb-3 pt-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E]">
                      Campaign Pulse
                    </div>
                    <div className="rounded-full border border-white/8 bg-white/[0.035] px-3 py-1 text-[9px] font-black uppercase tracking-[0.18em] text-[#9DA7C4]">
                      {pulseTotal} tracked
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[170px_minmax(0,1fr)] xl:items-center">
                    <div className="flex items-center justify-center">
                      <div className="relative flex h-[132px] w-[132px] items-center justify-center rounded-full border border-white/8 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.05),rgba(255,255,255,0.01)_65%,transparent_66%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
                        <svg viewBox="0 0 120 120" className="h-[104px] w-[104px] -rotate-90">
                          <circle
                            cx="60"
                            cy="60"
                            r="42"
                            fill="none"
                            stroke="rgba(255,255,255,0.08)"
                            strokeWidth="12"
                          />
                          {pulseSegments.length > 0 ? (
                            pulseSegments.map((segment) => (
                              <circle
                                key={`${campaign.id}-${segment.key}-pulse-ring`}
                                cx="60"
                                cy="60"
                                r="42"
                                fill="none"
                                stroke={segment.color}
                                strokeWidth="12"
                                strokeLinecap="round"
                                strokeDasharray={segment.dashArray}
                                strokeDashoffset={segment.dashOffset}
                              />
                            ))
                          ) : (
                            <circle
                              cx="60"
                              cy="60"
                              r="42"
                              fill="none"
                              stroke="rgba(125,211,252,0.35)"
                              strokeWidth="12"
                              strokeDasharray="10 12"
                            />
                          )}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="flex h-[68px] min-w-[68px] flex-col items-center justify-center rounded-full border border-white/10 bg-[#0B1018]/92 px-2 shadow-[0_10px_30px_rgba(0,0,0,0.28)]">
                            <div className={`${pulseTotalClassName} font-black leading-none text-white tabular-nums`}>
                              {pulseTotalLabel}
                            </div>
                            <div className="mt-1 text-[8px] font-black uppercase tracking-[0.18em] text-[#AAB6D3]">
                              Total
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {pulseStages.map((stage) => {
                        const share = pulseTotal > 0 ? Math.round((stage.value / pulseTotal) * 100) : 0;
                        const displayStageValue = stage.key === 'discarded' && stage.value > 0
                          ? `-${stage.value.toLocaleString()}`
                          : stage.value.toLocaleString();

                        return (
                          <div
                            key={`${campaign.id}-${stage.key}-pulse-legend`}
                            className="flex items-center justify-between gap-3 rounded-[16px] border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.025),rgba(255,255,255,0.01))] px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]"
                          >
                            <div className="flex min-w-0 items-center gap-2.5">
                              <span
                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                style={{
                                  backgroundColor: stage.color,
                                  boxShadow: `0 0 12px ${stage.color}55`,
                                }}
                              />
                              <span className="truncate text-[11px] font-semibold text-[#D7DEFF]">
                                {stage.label}
                              </span>
                            </div>
                            <div className="flex shrink-0 items-baseline gap-2">
                              <span className={`text-[12px] font-black tabular-nums ${stage.key === 'discarded' ? 'text-[#7F1D1D]' : 'text-white'}`}>
                                {displayStageValue}
                              </span>
                              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7E88A8]">
                                {share}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="rounded-[24px] border border-white/7 bg-[linear-gradient(180deg,rgba(255,255,255,0.018),rgba(255,255,255,0.006))] p-4 text-[#D7DEFF] xl:flex xl:min-h-full xl:flex-col"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    {ownerAvatar ?? (
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-sm font-black text-white/30">-</span>
                    )}
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#8E95B2]">
                        Account Owner
                      </div>
                      <p className="mt-2 truncate text-sm font-semibold text-white">{ownerLabel}</p>
                      <p className="mt-1 truncate text-[11px] text-[#7C86A1]">{ownerMeta}</p>
                    </div>
                  </div>
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${
                    isUnassigned
                      ? 'border-amber-300/20 bg-amber-300/10 text-amber-100'
                      : 'border-cyan-300/16 bg-cyan-300/10 text-cyan-100'
                  }`}>
                    {isUnassigned ? '!' : (ownerLabel[0] || '?').toUpperCase()}
                  </div>
                </div>

                <div className="mt-5">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E]">
                    Assign Campaign
                  </div>
                  <p className="mt-2 text-[12px] leading-5 text-[#8B96B0]">
                    Reassign without leaving the list.
                  </p>
                  <div className="mt-3">
                    <AccountManagerSelector
                      campaignId={campaign.id}
                      currentManagerId={campaign.account_manager_id}
                      teamMembers={teamMembers}
                    />
                  </div>
                </div>

                {(onOpenCampaign || onOpenDetails) && (
                  <div className="mt-5 xl:mt-auto xl:pt-5">
                    <ActionButtons
                      campaign={campaign}
                      onOpenCampaign={onOpenCampaign}
                      onOpenDetails={onOpenDetails}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className={cardClassName}
      onClick={handleOpenCampaign}
      onKeyDown={handleCardKeyDown}
      role={isCampaignInteractive ? 'button' : undefined}
      tabIndex={isCampaignInteractive ? 0 : undefined}
      aria-label={isCampaignInteractive ? `Open ${campaign.name}` : undefined}
    >
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),transparent_44%,transparent)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="relative flex h-full flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <CampaignStatusSelect campaign={campaign} className={pillClass} />
            {needsAssignment && (
              <span className="inline-flex rounded-full border border-amber-300/20 bg-amber-300/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-amber-100">
                Needs assignment
              </span>
            )}
          </div>

          {campaign.client_email && (
            <div className="max-w-[11rem] text-right">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#68758F]">Client</p>
              <p className="mt-1 truncate text-[12px] font-medium text-white/72">{campaign.client_email}</p>
            </div>
          )}
        </div>

        <div>
          {sellerLabel && (
            <p className="mb-1 truncate text-[11.5px] font-medium tracking-[0.01em] text-[#8B96B0]">
              {sellerLabel}
            </p>
          )}
          <h3 className="max-w-[18rem] text-[22px] font-black leading-tight text-white transition-colors group-hover:text-[#E5F8FF]">
            {campaign.name}
          </h3>
          <p className="mt-3 line-clamp-2 text-sm leading-6 text-[#95A0BA]">
            {objectiveText}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-[18px] border border-cyan-300/14 bg-[linear-gradient(180deg,rgba(34,211,238,0.10),rgba(34,211,238,0.04))] px-4 py-3">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100/72">Total Leads</div>
            <div className="mt-2 text-[30px] font-black leading-none text-white">{leadCount.toLocaleString()}</div>
            {discardedLeadDeltaLabel && (
              <div className="mt-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-[#7F1D1D] tabular-nums">
                {discardedLeadDeltaLabel} discarded
              </div>
            )}
          </div>
          <div className="rounded-[18px] border border-white/8 bg-white/[0.03] px-4 py-3">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#68758F]">Weekly</div>
            <div className="mt-2 truncate text-[14px] font-semibold text-white">{weeklyLimitLabel}</div>
            <div className="mt-1 truncate text-[11px] text-[#7C86A1]">Updated {updatedLabel}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {channelLabels.length > 0 ? (
            channelLabels.map((label) => (
              <span
                key={`${campaign.id}-${label}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/8 bg-white/[0.04] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-white/70"
              >
                <Icon icon="solar:hashtag-square-linear" className="h-3.5 w-3.5 text-cyan-100/65" aria-hidden="true" />
                {label}
              </span>
            ))
          ) : (
            <span className="inline-flex items-center rounded-full border border-dashed border-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-white/38">
              No channels listed
            </span>
          )}
        </div>

        <div
          className="rounded-[20px] border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-4"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#68758F]">Assign campaign</p>
              <p className="mt-1 truncate text-[14px] font-semibold text-white">{ownerLabel}</p>
              <p className="mt-1 truncate text-[12px] leading-5 text-[#8B96B0]">{ownerMeta}</p>
            </div>
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${
              needsAssignment
                ? 'border-amber-300/20 bg-amber-300/10 text-amber-100'
                : 'border-cyan-300/16 bg-cyan-300/10 text-cyan-100'
            }`}>
              {needsAssignment ? '!' : (ownerLabel[0] || '?').toUpperCase()}
            </div>
          </div>

          <AccountManagerSelector
            campaignId={campaign.id}
            currentManagerId={campaign.account_manager_id}
            teamMembers={teamMembers}
          />

          <div className="mt-4">
            <ActionButtons
              campaign={campaign}
              onOpenCampaign={onOpenCampaign}
              onOpenDetails={onOpenDetails}
            />
          </div>
        </div>
      </div>
    </article>
  );
};
